import {
  Dispatch,
  DragEvent,
  MouseEvent,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from 'react-beautiful-dnd'
import { useParams, useSearchParams } from 'react-router-dom'
import { Input, Typography } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { CurrentUser } from 'models/User'
import { useRecoilState } from 'recoil'
import {
  buildingFilesState,
  buildingModeState,
  listPointState,
  pendingDisplayImageState,
  revalidatePointDetailState,
  SelectedMode,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState
} from 'store/buildingStore'
import { currentPovState } from 'store/krpanoStore'
import useSWR from 'swr'
import { BuildingFile, OpenModalDelete, Point } from 'types/building'
import Krpano from 'utils/krpano'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import DeleteIcon from 'components/icons/DeleteIcon'
import LockIcon from 'components/icons/LockIcon'
import RotatedArrowIcon from 'components/icons/RotatedArrowIcon'

import MenuDropDownPoint from './MenuDropDownPoint'
import PointSubImage from './PointSubImage'

type Props = {
  point: Point
  pointIndex: number
  isOpenConfirm: string
  setIsOpenConfirm: Dispatch<SetStateAction<string>>
  setOpenMenu: Dispatch<SetStateAction<OpenModalDelete>>
}

export default function PointGroupImages({
  point,
  pointIndex,
  isOpenConfirm,
  setIsOpenConfirm,
  setOpenMenu
}: Props) {
  const { id, image360, order, isLocked, lockedByUser, lockedBy } = point

  const { id: buildingId, floorId } = useParams()
  const [searchParams] = useSearchParams()
  const isAnnotationDisplay = searchParams.get('is_display')

  const { getBuiildingType, getDirectory } = useDirectory()

  const [isExpanded, setIsExpanded] = useState(false)

  const [selectedSubImage, setSelectedSubImage] = useRecoilState(
    selectedSubImageState
  )
  const [pendingDisplayImage, setPendingDisplayImage] = useRecoilState(
    pendingDisplayImageState
  )

  const [points, setPoints] = useRecoilState(listPointState)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [currentSelectedPoint, setCurrentSelectedPoint] =
    useRecoilState(selectedPoint)
  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [buildingFiles, setBuildingFiles] = useRecoilState(buildingFilesState)

  const { data: profile } = useSWR<CurrentUser | null>('v1/users/me', {
    revalidateOnMount: false
  })
  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${buildingId}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )
  const { trigger: handleSubImages } = useMutation(
    `v1/points/${currentSelectedPoint}/sub-images`
  )
  const { trigger: handleMainImage } = useMutation(
    `v1/points/${currentSelectedPoint}/main-image`
  )
  const { trigger: handlePoint, isMutating: isMutatingPoint } =
    useMutation('v1/points')

  const { trigger: handleUpdateImageNote } = useMutation(
    `v1/points/${id}/images`
  )

  const { trigger: handleDeleteImageNote } = useMutation(
    `v1/points/${currentSelectedPoint}/images`,
    {},
    true
  )

  const [isEditingNote, setIsEditingNote] = useState(false)
  const [newNote, setNewNote] = useState(point?.image360?.note || '')
  const listSubImages = useMemo(() => {
    return currentSelectedPoint === pointDetail?.id
      ? [...(pointDetail?.subImages || [])].sort(
          (a, b) => Number(a.order) - Number(b.order)
        )
      : []
  }, [currentSelectedPoint, pointDetail?.id, pointDetail?.subImages])

  const toggleSubImagesList = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setIsEditingNote(false)
    if (!currentSelectedPoint || currentSelectedPoint !== point.id) {
      setCurrentSelectedPoint(point.id)
    }
    setIsExpanded((prev) => !prev)
  }

  const handleDisplayImage = (selectFile: BuildingFile) => {
    // Preserve current POV when switching Before & After images within the same point
    let initH = currentPov?.h ?? pointDetail?.initView?.h
    let initV = currentPov?.v ?? pointDetail?.initView?.v
    let initFov = currentPov?.z ?? pointDetail?.initView?.z

    if (!currentPov && selectFile.id !== pointDetail?.image360?.id) {
      const subImage = pointDetail?.subImages?.find(
        (subImage) => subImage.id === selectFile.id
      )
      initH = subImage?.initView?.h
      initV = subImage?.initView?.v
      initFov = subImage?.initView?.z
    }

    const krpano = document.getElementById('embedpano-full') as any
    if (!krpano || !pointDetail) return
    const { isLocked } = pointDetail
    const { multires } = selectFile
    const tilesUrl = selectFile.tilesUrl || ''
    const imageUrl = selectFile.s3Path
    const previewUrl = selectFile.thumbnail
    const sceneName = Krpano.getScenceNameByFileId(
      currentSelectedPoint,
      points,
      selectFile.id
    )
    if (krpano.get(`scene[${sceneName}]`) === null) {
      const screenString = Krpano.generateSceneXML({
        sceneName,
        tilesUrl: tilesUrl || undefined,
        multires: multires || undefined,
        s3Path: imageUrl,
        thumbnailUrl: previewUrl || ''
      })
      krpano.call(`loadxml(${screenString}, null, MERGE);`)
    }

    // krpano.call(`loadscene(${sceneName}, null, MERGE, 'simple crossblending');`)
    krpano.call(`load_scene(${sceneName},${initH},${initV},${initFov})`)

    // Always re-add all annotations after image load
    const annotations = pointDetail?.annotations?.filter((item: any) => {
      // An annotation should be displayed if:
      // 1. It has no specific imageID (belongs to the whole point).
      // 2. Its imageID matches the image's ID.
      return (
        (pointDetail?.image360?.id === selectFile.id && !item.imageID) ||
        item.imageID === selectFile.id
      )
    })
    const directions = pointDetail?.directions
      ?.map((dir) => {
        const order = points.find(
          (p) => p.id !== currentSelectedPoint && dir.id.includes(p.id)
        )?.order
        return !order ? { ...dir, target: null } : { ...dir, target: order }
      })
      .filter((dir: any) => dir.target)
    const stairways = pointDetail?.stairways?.map((stair) => ({
      ...stair,
      nextFloorName: building?.floors.find(
        (floor) => floor.id === +stair.nextFloorId
      )?.name
    }))

    annotations &&
      Krpano.AddAnnotation(
        krpano,
        annotations,
        isAnnotationDisplay,
        getBuiildingType() === 10
      )
    directions && Krpano.AddDirectionArrow(krpano, directions)
    stairways && Krpano.AddStairway(krpano, stairways)
    Krpano.TogglePointLock(isLocked)
  }

  const handleSelectSubImage = (item: BuildingFile) => {
    Krpano.LoadChecking(() => {
      setSelectedSubImage(item.id)
      setPendingDisplayImage(item)
    })
  }

  useEffect(() => {
    // avoid useEffect multiple times in other point
    if (id !== currentSelectedPoint) {
      return
    }
    if (pendingDisplayImage && selectedSubImage === pendingDisplayImage.id) {
      handleDisplayImage(pendingDisplayImage)
      setPendingDisplayImage(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubImage, pendingDisplayImage])

  useEffect(() => {
    setIsEditingNote(false)
  }, [currentSelectedPoint])

  const handleSelectPoint = (item: Point) => () => {
    if (
      !points.find((point) => point.id === item.id)?.image360 &&
      buildingMode?.previewMode
    ) {
      return
    }
    if (
      !isOpenConfirm &&
      currentSelectedPoint !== item.id &&
      buildingMode.selectedMode === 'direction'
    ) {
      setIsOpenConfirm('point')
      return
    }
    if (currentSelectedPoint === item.id) {
      Krpano.LoadChecking(() => {
        !buildingMode.previewMode && setCurrentSelectedPoint(undefined)
        if (selectedSubImage) {
          setSelectedSubImage(undefined)
          if (pointDetail?.image360) {
            handleDisplayImage(pointDetail.image360)
          }
        }
      })
      return
    }
    Krpano.LoadChecking(() => {
      currentPov && setCurrentPov(null)
      setSelectedSubImage(undefined)
      setBuildingMode((prev) => ({ ...prev, selectedMode: SelectedMode.NONE }))
      setCurrentSelectedPoint(item.id)
      currentPov && setCurrentPov(null)
      pointDetail?.id !== item.id && setPointDetail(undefined)
    })
  }

  const handleDragEnd = (res: DropResult) => {
    if (!res.destination || !pointDetail?.image360) return

    const newSubImages = [...(listSubImages || [])]
    const [reorderedItem] = newSubImages.splice(res.source.index, 1)
    if (res.destination?.droppableId === 'mainImages_droppable') {
      newSubImages.splice(res.destination.index, 0, {
        ...pointDetail.image360,
        order: reorderedItem.order
      })
      setPoints((prev) =>
        prev.map((item) =>
          item.id === currentSelectedPoint
            ? { ...item, image360: reorderedItem }
            : item
        )
      )
      setPointDetail(
        (prev) =>
          prev && {
            ...prev,
            image360: reorderedItem,
            subImages: newSubImages
          }
      )
      handleMainImage({
        data: {
          buildingId: Number(buildingId),
          floorId: Number(floorId),
          imageId: reorderedItem.id
        }
      })
      return
    }
    newSubImages.splice(res.destination.index, 0, reorderedItem)
    setPointDetail(
      (prev) =>
        prev && {
          ...prev,
          subImages: newSubImages.map((item, index) => ({
            ...item,
            order: index + 1
          }))
        }
    )

    handleSubImages({
      method: 'patch',
      data: {
        buildingId: Number(buildingId),
        floorId: Number(floorId),
        subImages: newSubImages.map((item) => item.id)
      }
    })
  }

  const handleDropSubImage = async (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const receivedData = e.dataTransfer?.getData('text') || ''
    const subImageId = receivedData.split('-')[1]
    if (!isMutatingPoint && buildingId && floorId) {
      const payload = image360
        ? { fileID: +subImageId, buildingId: +buildingId, floorId: +floorId }
        : {
            image360: +subImageId,
            floor_id: +floorId,
            building_id: +buildingId
          }
      try {
        await handlePoint({
          method: image360 ? 'post' : 'patch',
          query: image360 ? [id, 'sub-images'] : [id],
          data: {
            ...payload,
            floor_id: +floorId,
            building_id: +buildingId
          }
        })
        if (image360) {
          setPoints((prev) =>
            prev.map((item) =>
              item.id === id ? { ...item, isSubImages: true } : item
            )
          )
          setBuildingFiles((prev) => ({
            ...prev,
            files: prev.files.map((file) =>
              file.id === +subImageId ? { ...file, pointId: id } : file
            )
          }))
        } else {
          const newPointList = points.map((point) =>
            point.id === id
              ? {
                  ...point,
                  image360:
                    buildingFiles.files.find(
                      (file) => file.id === +subImageId
                    ) || null
                }
              : point
          )
          const newFileList = buildingFiles.files.map((file) =>
            file.id === image360 ? { ...file, pointId: id } : file
          )
          setPoints(newPointList)
          setBuildingFiles((prev) => ({
            ...prev,
            files: newFileList
          }))
        }
        id === currentSelectedPoint && setRevalidatePointDetail(id)
      } catch (error) {
        id === currentSelectedPoint && setRevalidatePointDetail(id)
      }
    }
  }

  useEffect(() => {
    if (currentSelectedPoint !== point.id) {
      setIsExpanded(false)
    } else if (selectedSubImage && point.isSubImages) {
      setIsExpanded(true)
    }
  }, [currentSelectedPoint, point.id, selectedSubImage, point.isSubImages])

  const saveNoteImage = async () => {
    if (newNote) {
      try {
        await handleUpdateImageNote({
          method: 'patch',
          data: {
            buildingId: Number(buildingId),
            fileId: image360?.id,
            note: newNote
          }
        })
        setIsEditingNote(false)
        setPoints((prev) =>
          prev.map((item) =>
            item.id === id && item.image360
              ? {
                  ...item,
                  image360: {
                    ...item.image360,
                    note: newNote
                  }
                }
              : item
          )
        )
      } catch (error) {
        setIsEditingNote(false)
      }
    } else {
      try {
        await handleDeleteImageNote({
          method: 'patch',
          data: {
            buildingId: Number(buildingId),
            fileId: image360?.id,
            note: ''
          }
        })
        setIsEditingNote(false)
        setPoints((prev) =>
          prev.map((item) =>
            item.id === id && item.image360
              ? {
                  ...item,
                  image360: {
                    ...item.image360,
                    note: ''
                  }
                }
              : item
          )
        )
      } catch (error) {
        setIsEditingNote(false)
      }
    }
  }

  const handleClickNote = () => {
    setCurrentSelectedPoint(point.id)
    setSelectedSubImage(undefined)
    const placeHolder =
      point.image360?.note ||
      (point.image360?.takenAt &&
        // eslint-disable-next-line prettier/prettier
        dayjs(point.image360?.takenAt)
          .utc()
          .format('YYYY-MM-DD HH:mm')) ||
      ''

    requestAnimationFrame(() => {
      setNewNote(placeHolder)
      setIsEditingNote(true)
    })
  }

  return (
    <Draggable draggableId={`drag ${id}`} index={pointIndex}>
      {(pointProvided: any) => (
        <div
          className="relative"
          id={`point-group-${id}`}
          ref={pointProvided.innerRef}
          {...pointProvided.draggableProps}
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="mainImages_droppable">
              {(mainDroppableProvided) => (
                <>
                  <div
                    className="absolute top-0 left-0 w-full h-20"
                    {...mainDroppableProvided.droppableProps}
                    ref={mainDroppableProvided.innerRef}
                  >
                    {mainDroppableProvided.placeholder}
                  </div>
                  <div
                    {...pointProvided.dragHandleProps}
                    className={clsx(
                      'absolute top-0 left-0',
                      'p-[10px] min-h-[36px] border-0 border-b border-solid theme-border-secondary !cursor-pointer relative',
                      {
                        'theme-bg-sub-primary':
                          currentSelectedPoint === id && !selectedSubImage
                      },
                      {
                        'theme-bg-third':
                          currentSelectedPoint !== id || selectedSubImage
                      }
                    )}
                    onClick={handleSelectPoint(point)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropSubImage}
                  >
                    <div className="flex justify-between">
                      <div
                        className={`absolute ${
                          image360
                            ? 'top-[15px] left-[16px]'
                            : 'top-[10px] left-[10px]'
                        } z-10 rounded-lg flex bg-[#021120] p-[2px] min-w-[18px] h-[18px]`}
                      >
                        <div className="m-auto text-[13px] leading-[13px] font-bold text-white">
                          {order}
                        </div>
                      </div>
                      <div className="absolute left-10 top-[15px] z-10 flex items-center gap-2">
                        {point.isSubImages && (
                          <div
                            className="rounded-full flex bg-[#787B82CC] w-[18px] h-[18px]"
                            onClick={toggleSubImagesList}
                          >
                            <RotatedArrowIcon
                              className={`m-auto ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        )}
                        {point.image360 && getDirectory() === 'tour' && (
                          <div className="ant-typography text-white text-[12px] max-w-[100px] bg-[#02112070]">
                            <Typography.Text
                              className="text-[12px] text-white truncate cursor-pointer"
                              ellipsis={{ tooltip: point.image360.name }}
                            >
                              {point.image360?.name}
                            </Typography.Text>
                          </div>
                        )}
                      </div>

                      {!buildingMode.previewMode && image360 && !isLocked && (
                        <div className="absolute top-[10px] right-[10px] z-20">
                          <MenuDropDownPoint
                            pointId={id}
                            pointOrder={order}
                            profile={profile}
                            image360={image360}
                            setNewNote={setNewNote}
                          />
                        </div>
                      )}

                      {!buildingMode.previewMode && !image360 && (
                        <div
                          className="cursor-pointer w-5 h-5 absolute top-[10px] right-[10px] z-20 flex justify-center items-center"
                          onClick={(e: any) => {
                            e.stopPropagation()
                            setOpenMenu({
                              isOpen: 'handleDeletePoint',
                              pointId: id,
                              pointOrder: order
                            })
                          }}
                        >
                          <DeleteIcon />
                        </div>
                      )}

                      {image360 && isLocked && (
                        <div
                          className={`grid grid-cols-[1fr_auto] items-end gap-1 absolute ${
                            id === currentSelectedPoint
                              ? 'bottom-[10px] left-[10px]'
                              : 'bottom-[10px] left-[12px]'
                          } z-20 max-w-[180px]`}
                        >
                          <div
                            className={clsx(
                              {
                                'w-[33px] h-[30px] theme-bg-sub-primary rounded-tr-md shadow-[0_0_4px_0_#00000040] flex items-center justify-center':
                                  id === currentSelectedPoint
                              },
                              {
                                'w-[27px] h-[27px] rounded-full flex justify-center items-center bg-[#787B82]':
                                  id !== currentSelectedPoint
                              }
                            )}
                          >
                            <LockIcon isSelfLocked={lockedBy === profile?.id} />
                          </div>
                          <Typography.Text
                            className="text-[12px] leading-[18px] text-white px-1 truncate bg-[#02112070]"
                            ellipsis={{
                              tooltip: lockedByUser
                                ? `${lockedByUser.lastName} ${lockedByUser.firstName}`
                                : ''
                            }}
                          >
                            {lockedByUser
                              ? `${lockedByUser.lastName} ${lockedByUser.firstName}`
                              : ''}
                          </Typography.Text>
                        </div>
                      )}
                    </div>

                    {image360?.id && (
                      <LazyThumbnail
                        imageType={ImageType.OBJECT_FILL}
                        url={
                          image360.multires
                            ? `${image360.tilesUrl}thumb.jpg`
                            : image360.thumbnail
                        }
                        name={image360.name}
                      />
                    )}
                    {point?.image360 && !point?.image360?.note ? (
                      <div className="ant-typography  text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px]">
                        <Typography.Text
                          className="text-[12px] text-white"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleClickNote()
                          }}
                        >
                          {dayjs(point?.image360?.takenAt)
                            ?.utc()
                            .format('YYYY-MM-DD HH:mm')}
                        </Typography.Text>
                      </div>
                    ) : (
                      <div className="ant-typography text-white text-[12px] max-w-[100px] bg-[#02112070] absolute right-[10px] bottom-[10px]">
                        <Typography.Text
                          className="text-[12px] text-white truncate cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleClickNote()
                          }}
                          ellipsis={{ tooltip: point.image360?.note }}
                        >
                          {point.image360?.note}
                        </Typography.Text>
                      </div>
                    )}
                    {isEditingNote && (
                      <div className="absolute z-50 bottom-0 right-2 w-[200px]">
                        <Input
                          className="w-full"
                          value={newNote}
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                          onChange={(e) => {
                            setNewNote(e.target.value)
                          }}
                          onBlur={() => {
                            setIsEditingNote(false)
                            setNewNote(point.image360?.note || '')
                          }}
                          onPressEnter={saveNoteImage}
                          autoFocus
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </Droppable>
            {isExpanded && (
              <Droppable droppableId="subImages_droppable">
                {(listSubImageProvided: any) => (
                  <div
                    {...listSubImageProvided.droppableProps}
                    ref={listSubImageProvided.innerRef}
                  >
                    {listSubImages.map((subImage, index) => (
                      <Draggable
                        key={subImage.id}
                        draggableId={`drag-${subImage.id}`}
                        index={index}
                      >
                        {(subImageProvided: any) => (
                          <div
                            ref={subImageProvided.innerRef}
                            {...subImageProvided.draggableProps}
                            {...subImageProvided.dragHandleProps}
                          >
                            <PointSubImage
                              isSelected={selectedSubImage === subImage.id}
                              subImage={subImage}
                              handleSelectSubImage={handleSelectSubImage}
                              profile={profile || null}
                            />
                            {subImageProvided.placeholder}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {listSubImageProvided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </DragDropContext>
          {pointProvided.placeholder}
        </div>
      )}
    </Draggable>
  )
}
